import { throttle } from 'lodash';
import { action, autorun, computed, IReactionDisposer, observable, reaction } from 'mobx';
import { disposeOnUnmount, observer } from 'mobx-react';
import * as pbi from 'powerbi-client';
import * as React from 'react';
import { BsBriefcase } from '@react-icons/all-files/bs/BsBriefcase';
import { autoinject, inject } from '../Utils/AutoInject';
import { bind } from '../Utils/Bind';
import { componentAutoReaction } from '../Utils/MobxTools';
import { PowerBIStore } from '../Utils/PowerBIStore';
import { RetryPolicy } from '../Utils/Retry';
import { Alert, AlertHandle } from './Alert';
import { NothingHere } from './NothingHere';

const darkTheme = require('../Styles/powerbi-dark-theme.json'); // eslint-disable-line
const lightTheme = require('../Styles/powerbi-light-theme.json'); // eslint-disable-line
declare let powerbi: pbi.service.Service;

export interface IBiReportProps {
    onTileClicked?(event: pbi.service.ICustomEvent<any>): void;
    onEmbedded?(embed: pbi.Embed): void;
    reportOrDashboardId: string;
    type: 'report' | 'dashboard';
    pageName?: string;
    filters?: pbi.models.IFilter[];
    filterPaneEnabled?: boolean;
    navContentPaneEnabled?: boolean;
    darkTheme?: boolean;
}

@autoinject
@observer
export class BiReport extends React.Component<IBiReportProps> {
    @action
    public componentDidMount() {
        const trackedValues = computed(() => [this.props.reportOrDashboardId, this.props.type]);
        componentAutoReaction(this, () => trackedValues.get(), this.load);
    }

    public componentWillUnmount() {
        this.reset();
    }

    public render() {
        const { reportOrDashboardId } = this.props;

        const empty = !reportOrDashboardId || !this.reportOrDashboard;

        return (
            <>
                <Alert alertHandle={this.alertHandle} />
                <NothingHere
                    empty={empty}
                    loading={this.loading}
                    forceLoaderOnLoad
                    icon={<BsBriefcase />}
                    label="No Data Available"
                    loadingMessage="Loading Report..."
                />
                <div
                    className={`powerbi-frame ${this.loading ? 'd-none' : ''}`}
                    id="reportContainer"
                    ref={this.bindElement}
                />
            </>
        );
    }

    @bind
    @action
    private async load() {
        const { reportOrDashboardId, type } = this.props;

        if (!reportOrDashboardId) return;

        console.debug(`load ${reportOrDashboardId} ${type}`);

        this.loading = true;
        try {
            switch (type) {
                case 'dashboard':
                    await this.retryPolicy.execute(() =>
                        this.powerBIStore.loadPowerBiDashboardAsync(reportOrDashboardId)
                    );
                    break;
                case 'report':
                    await this.retryPolicy.execute(() => this.powerBIStore.loadPowerBiReportAsync(reportOrDashboardId));
                    break;
            }
        } finally {
            this.loading = false;
        }

        // Start embed disposer only after initial load
        if (!this.embedDisposer) {
            this.embedDisposer = reaction(() => this.embedConfiguration, this.embed, { fireImmediately: true });
        }
    }

    @bind
    @action
    private embed() {
        if (!this.rootElement) {
            console.warn('embed: powerbi-frame does not exist');
            return;
        }

        if (!this.validConfig) {
            console.warn('embed: invalid config');
            this.alertHandle.set('danger', 'Error in PowerBI configuration');
            return;
        }

        if (!this.bootstrapped) powerbi.reset(this.rootElement);

        this.bootstrapped = false;

        console.debug('embed', this.rootElement, this.embedConfiguration);

        if (this.embedConfiguration) {
            try {
                this.component = powerbi.embed(this.rootElement, this.embedConfiguration);

                if (this.component) {
                    if (this.props.onTileClicked) {
                        this.component.on('tileClicked', this.props.onTileClicked);
                    }

                    if (this.props.onEmbedded) {
                        this.props.onEmbedded(this.component);
                    }
                }

                this.rootElement.addEventListener('visualClicked', this.throttledKeepAlive);
                this.rootElement.addEventListener('error', this.onError);
            } catch (e: any) {
                console.error('powerbiembed', e);
            }
        }
    }

    @bind
    @action
    private reset() {
        if (this.rootElement) {
            powerbi.reset(this.rootElement);
            this.rootElement.removeEventListener('error', this.onError);
        }
        this.component = null;
    }

    @computed
    private get embedConfiguration(): pbi.IEmbedConfiguration | undefined {
        if (!this.reportOrDashboard) return;

        const { type, reportOrDashboardId, pageName, filters, filterPaneEnabled, navContentPaneEnabled } = this.props;
        const { token } = this.reportOrDashboard.tokenData;
        const { embedUrl } = this.reportOrDashboard.report ?? this.reportOrDashboard.dashboard;

        const dashboardId = type == 'dashboard' ? reportOrDashboardId : undefined;

        return {
            type,
            id: reportOrDashboardId,
            dashboardId,
            tokenType: pbi.models.TokenType.Embed,
            accessToken: token,
            embedUrl,
            pageName,
            filters,
            settings: {
                filterPaneEnabled,
                navContentPaneEnabled,
                panes: {
                    pageNavigation: {
                        visible: true,
                        position: pbi.models.PageNavigationPosition.Left,
                    },
                },
            },
            theme: this.theme,
        };
    }

    @computed
    private get theme() {
        return this.props.darkTheme ? darkTheme : lightTheme;
    }

    @computed
    private get validConfig() {
        if (!this.embedConfiguration) return false;

        const errors = pbi.models.validateReportLoad(this.embedConfiguration);

        if (errors) console.error('powerbi errors', errors);

        return errors === undefined;
    }

    @computed
    private get reportOrDashboard() {
        const { reportOrDashboardId, type } = this.props;
        switch (type) {
            case 'dashboard':
                return this.powerBIStore.dashboards.get(reportOrDashboardId);
                break;
            case 'report':
                return this.powerBIStore.reports.get(reportOrDashboardId);
                break;
        }
    }

    @bind
    @action
    private bindElement(ref: HTMLDivElement | null) {
        if (this.rootElement == undefined && ref && !this.bootstrapped) {
            powerbi.bootstrap(ref, {
                type: this.props.type,
                hostname: 'https://app.powerbi.com',
            });
            this.bootstrapped = true;
        }

        this.rootElement = ref;
    }

    @bind
    @action
    private onError(event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) {
        if (event instanceof CustomEvent) event.preventDefault();
        console.error(event);
        return true;
    }

    private bootstrapped = false;
    private component: pbi.Embed | null = null;
    private rootElement: HTMLDivElement | null = null;
    @disposeOnUnmount
    private embedDisposer?: IReactionDisposer;
    private alertHandle = new AlertHandle();
    @observable
    private loading = false;
    private readonly retryPolicy = new RetryPolicy<void>(
        2,
        () => 3000,
        undefined,
        e => e.exceptionType == 'Overloaded'
    );

    @inject
    private powerBIStore!: PowerBIStore;

    private throttledKeepAlive = throttle(this.powerBIStore.sendKeepAliveAsync, 15000);
}
